import Variant from './variant';
import Mutation from './mutation';

import { AA_SIZE, TAU, BY_GENOME} from './common';
import { POSITIONS } from './sequtil';
import { LIGHT_GENE_BG, LIGHT_GENE_BG_MID, LIGHT_GENE_BG_BACK,
  DARK_GENE_BG, DARK_GENE_BG_MID, DARK_GENE_BG_BACK,
  SEQUENCE_TICK_STROKE_WEIGHT } from './styles';


export default class Sequence extends Variant {
  constructor({name, byRegion, unsequencedRegions, mutations}) {
    super(name, 1);
    this.label = name.replace(/_/g, ' ');
    this.allPositions = byRegion;
    this.unsequencedRegions = unsequencedRegions;
    this.mutationList = mutations;
    mutations.forEach(m=>{
      try {
        this.mutations[m] = {pct: 1, count: 1};
        let mutation = new Mutation(m);
        this.genes[mutation.gene][mutation.position][0] = {mutation: mutation, count: 1, pct: 1};
      } catch (err) {
        console.log(m, err);
      }
    });
    this.unsequencedGeneFragments = {};
    for (let gene in POSITIONS) {
      let {start, end} = POSITIONS[gene];
      unsequencedRegions.forEach(([startIndex, endIndex])=>{
        if (startIndex <= end && endIndex >= start) {
          /* this gene has at least some part of it unsequenced */
          if (this.unsequencedGeneFragments[gene] === undefined) {
            this.unsequencedGeneFragments[gene] = [];
          }
          startIndex = Math.max(start, startIndex);
          endIndex = Math.min(end, endIndex);
          let geneSize = end - start,
            startPct = (startIndex - start) / geneSize,
            endPct = (endIndex - start) / geneSize;
          // console.log(gene, start, end, startIndex, endIndex, startPct, endPct);
          this.unsequencedGeneFragments[gene].push([startPct, endPct, startIndex, endIndex]);
        }
      });
    }
  }

  getPercent(name) {
    return this.mutations[name] === undefined ? 0 : this.mutations[name].pct;
  }

  getCount(name) {
    return this.mutations[name] === undefined ? 0 : this.mutations[name].count;
  }


  setUnsequencedMutationAreas(geneWedges) {
    /* do any of the mutations we will show land in unsequenced areas? */
    this.unsequencedGeneMutationRanges = {};
    geneWedges.forEach(gw=>{
      let gName = gw.name;
      gw.visibles.forEach(({mutation}, i)=>{
        let startPos = POSITIONS[mutation.gene].start + AA_SIZE * mutation.position,
          endPos = startPos + AA_SIZE;
        this.unsequencedRegions.forEach(([start, end])=>{
          if (startPos <= end && endPos >= start) {
            if (this.unsequencedGeneMutationRanges[gName] === undefined) {
              this.unsequencedGeneMutationRanges[gName] = [];
            }
            this.unsequencedGeneMutationRanges[gName].push(i);
          }
        });
      });
      if (this.unsequencedGeneMutationRanges[gName]) {
        let skipIndices = this.unsequencedGeneMutationRanges[gName],
          skipRegions = [],
          mutCount = gw.visibles.length,
          skipStart = -1,
          prev = -1;
        console.log(mutCount, skipIndices);
        skipIndices.forEach(index=>{
          if (index === prev + 1) {
            if (skipStart === -1) {
              skipStart = index;
            }
          } else {
            if (skipStart >= 0) {
              // console.log('skipping indices', skipStart, prev);
              skipRegions.push([skipStart / mutCount, (prev + 1) / mutCount, skipStart, prev]);
            }
            skipStart = -1;
          }
          prev = index;
        });
        if (skipStart>=0) {
          // console.log('skipping indices', skipStart, prev);
          skipRegions.push([skipStart / mutCount, (prev + 1) / mutCount, skipStart, prev]);
        }
        this.unsequencedGeneMutationRanges[gName] = {skipIndices, skipRegions};
      }
    })
  }

  drawGeneWedge(ctx, bySequence, darkMode, highlightGene, geneWedge, highlightRing) {
    if (bySequence === BY_GENOME) {
      if (!this.unsequencedGeneFragments[geneWedge.name]) super.drawGeneWedge(ctx, bySequence, darkMode, highlightGene, geneWedge);
      else this.drawUnseqWedgeGenome(ctx, bySequence, geneWedge, highlightRing, highlightGene, darkMode);
    } else {
      if (!this.unsequencedGeneMutationRanges[geneWedge.name]) super.drawGeneWedge(ctx, bySequence, darkMode, highlightGene, geneWedge);
      else this.drawUnseqWedgeMutation(ctx, bySequence, geneWedge, highlightRing, highlightGene, darkMode);
    }
  }

  drawUnseqWedgeGenome(ctx, bySequence, geneWedge, highlightRing, highlightGene, darkMode) {
    let {outer} = this,
      // mid = (inner + outer) / 2,
      unseq = this.unsequencedGeneFragments[geneWedge.name],
      {sa, ea, ce, se} = geneWedge;
      // strokeWidth = (bySequence === BY_GENOME ? SEQUENCE_TICK_STROKE_WEIGHT
      //   : bySequence === BY_MUTATION ? MUTATION_TICK_STROKE_WEIGHT
      //   : MUTATION_BLOCK_STROKE_WEIGHT) * 0.5;
    ctx.fillStyle = highlightRing === null ? LIGHT_GENE_BG
      : highlightRing !== this ? LIGHT_GENE_BG_BACK
        : geneWedge.name === highlightGene ? LIGHT_GENE_BG
          : LIGHT_GENE_BG_MID;
    if (darkMode) {
      ctx.fillStyle = highlightRing === null ? DARK_GENE_BG
        : highlightRing !== this ? DARK_GENE_BG_BACK
          : geneWedge.name === highlightGene ? DARK_GENE_BG
            : DARK_GENE_BG_MID;
    }
    ctx.strokeStyle = ctx.fillStyle;
    let priorEndA = sa,
      // pe = 0, ps,
      wedgeRange = ea - sa,
      {start} = POSITIONS[geneWedge.name];
    unseq.forEach(([startPct, endPct])=>{
      let a1 = sa + (startPct * wedgeRange),
        a2 = sa + (endPct * wedgeRange),
        cosA1 = Math.cos(a1),
        sinA1 = Math.sin(a1);
      while (a2 < a1) a2 += TAU;
      if (start > priorEndA) {
        // console.log('draw wedge', geneWedge.name, start, end, sa, ea);
        // console.log('          ', geneWedge.name, startIndex, endIndex, priorEndA, a1);
        this.drawWedge(ctx, bySequence, darkMode, highlightGene, priorEndA, a1, Math.cos(priorEndA), Math.sin(priorEndA), cosA1, sinA1, geneWedge.name);
      }
      ctx.lineWidth = SEQUENCE_TICK_STROKE_WEIGHT;
      ctx.beginPath();
      ctx.moveTo(cosA1 * outer, sinA1 * outer);
      ctx.arc(0, 0, outer, a1, a2);
      ctx.stroke();
      priorEndA = a2;
      // ps = startIndex;
      // pe = endIndex;
    });
    if (priorEndA < ea) {
      while (priorEndA > ea) {
        ea += TAU;
      }
      // console.log('draw wedge', geneWedge.name, priorEndA, ea, ps, pe, start, end);
      this.drawWedge(ctx, bySequence, darkMode, highlightGene, priorEndA, ea, Math.cos(priorEndA), Math.sin(priorEndA), ce, se, geneWedge.name);
    }
  }


  drawUnseqWedgeMutation(ctx, bySequence, geneWedge, highlightRing, highlightGene, darkMode) {
    let {outer} = this,
      // mid = (inner + outer) / 2,
      unseq = this.unsequencedGeneMutationRanges[geneWedge.name].skipRegions,
      {sa, ea, ce, se, visibles} = geneWedge;
      // strokeWidth = (bySequence === BY_GENOME ? SEQUENCE_TICK_STROKE_WEIGHT
      //   : bySequence === BY_MUTATION ? MUTATION_TICK_STROKE_WEIGHT
      //   : MUTATION_BLOCK_STROKE_WEIGHT) * 0.5;
    if (ea > sa || visibles.length > 0) {

      ctx.fillStyle = highlightRing === null ? LIGHT_GENE_BG
        : highlightRing !== this ? LIGHT_GENE_BG_BACK
          : geneWedge.name === highlightGene ? LIGHT_GENE_BG
            : LIGHT_GENE_BG_MID;
      if (darkMode) {
        ctx.fillStyle = highlightRing === null ? DARK_GENE_BG
          : highlightRing !== this ? DARK_GENE_BG_BACK
            : geneWedge.name === highlightGene ? DARK_GENE_BG
              : DARK_GENE_BG_MID;
      }
      ctx.strokeStyle = ctx.fillStyle;
      let priorEndA = sa,
        // pe = 0, ps,
        wedgeRange = ea - sa,
        {start } = POSITIONS[geneWedge.name];
      unseq.forEach(([startPct, endPct])=>{
        let a1 = sa + (startPct * wedgeRange),
          a2 = sa + (endPct * wedgeRange),
          cosA1 = Math.cos(a1),
          sinA1 = Math.sin(a1);
        while (a2 < a1) a2 += TAU;
        if (start > priorEndA) {
          // console.log('draw wedge', geneWedge.name, start, end, sa, ea);
          // console.log('          ', geneWedge.name, startIndex, endIndex, priorEndA, a1);
          // this.drawWedge(priorEndA, a1, Math.cos(priorEndA), Math.sin(priorEndA), cosA1, sinA1, geneWedge.name);
          this.drawWedge(ctx, bySequence, darkMode, highlightGene, priorEndA, a1, Math.cos(priorEndA), Math.sin(priorEndA), cosA1, sinA1);
        }
        // ctx.lineWidth = strokeWidth;
        ctx.lineWidth = SEQUENCE_TICK_STROKE_WEIGHT;
        ctx.beginPath();
        ctx.moveTo(cosA1 * outer, sinA1 * outer);
        ctx.arc(0, 0, outer, a1, a2);
        ctx.stroke();
        priorEndA = a2;
        // ps = startIndex;
        // pe = endIndex;
      });
      if (priorEndA < ea) {
        while (priorEndA > ea) {
          ea += TAU;
        }
        // console.log('draw wedge', geneWedge.name, priorEndA, ea, ps, pe, start, end);
        this.drawWedge(ctx, bySequence, darkMode, highlightGene, priorEndA, ea, Math.cos(priorEndA), Math.sin(priorEndA), ce, se);
      }
    }
  }

}
